import React, { useEffect, useMemo, useState } from "react";
import ReactQuill from "react-quill";
import { toast } from "react-toastify";
import { FaImage, FaSave, FaTimes } from "react-icons/fa";

import "react-quill/dist/quill.snow.css";

import {
  buildInitialHistoryForm,
  getMediaUrl,
  normalizeHistoryPayload,
  validateHistoryForm,
} from "../utils/leClubUtils";

const inputClass =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-orange-400 focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-orange-500/10";

const labelClass =
  "mb-1.5 block text-sm font-bold text-slate-700 dark:text-slate-200";

function ImagePreview({ url, label }) {
  const imageUrl = getMediaUrl(url);

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={label}
          className="h-40 w-full object-cover"
        />
      ) : (
        <div className="flex h-40 flex-col items-center justify-center text-slate-400">
          <FaImage className="text-3xl" />
          <p className="mt-2 text-xs font-medium">Aucune image</p>
        </div>
      )}
    </div>
  );
}

export default function HistoryForm({
  settings,
  saving = false,
  onSubmit,
  onClose,
}) {
  const initialForm = useMemo(
    () => buildInitialHistoryForm(settings),
    [settings]
  );

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    setFormData(initialForm);
  }, [initialForm]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = normalizeHistoryPayload(formData);
    const errorMessage = validateHistoryForm(payload);

    if (errorMessage) {
      toast.error(errorMessage);
      return;
    }

    await onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Images de l’historique
        </h3>

        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Renseignez jusqu’à 3 images. Les chemins relatifs `/uploads/...` sont
          acceptés.
        </p>

        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          <div className="space-y-3">
            <ImagePreview
              url={formData.historique_photo_url}
              label="Historique photo 1"
            />

            <div>
              <label className={labelClass}>Photo 1</label>
              <input
                type="text"
                name="historique_photo_url"
                value={formData.historique_photo_url}
                onChange={handleChange}
                placeholder="/uploads/... ou https://..."
                className={inputClass}
              />
            </div>
          </div>

          <div className="space-y-3">
            <ImagePreview
              url={formData.historique_photo2_url}
              label="Historique photo 2"
            />

            <div>
              <label className={labelClass}>Photo 2</label>
              <input
                type="text"
                name="historique_photo2_url"
                value={formData.historique_photo2_url}
                onChange={handleChange}
                placeholder="/uploads/... ou https://..."
                className={inputClass}
              />
            </div>
          </div>

          <div className="space-y-3">
            <ImagePreview
              url={formData.historique_photo3_url}
              label="Historique photo 3"
            />

            <div>
              <label className={labelClass}>Photo 3</label>
              <input
                type="text"
                name="historique_photo3_url"
                value={formData.historique_photo3_url}
                onChange={handleChange}
                placeholder="/uploads/... ou https://..."
                className={inputClass}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
        <h3 className="text-base font-bold text-slate-950 dark:text-white">
          Texte de présentation
        </h3>

        <div className="mt-4">
          <label className={labelClass}>
            Titre de l’historique <span className="text-rose-500">*</span>
          </label>
          <input
            type="text"
            name="historique_title"
            value={formData.historique_title}
            onChange={handleChange}
            placeholder="Ex : L’histoire de notre club"
            className={inputClass}
          />
        </div>

        <div className="mt-4">
          <label className={labelClass}>Contenu enrichi</label>

          <div className="rounded-2xl bg-white dark:bg-slate-950">
            <ReactQuill
              value={formData.historique_content}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  historique_content: value,
                }))
              }
              className="min-h-[300px] rounded-2xl bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100"
              theme="snow"
            />
          </div>
        </div>
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Annuler
          </button>

          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaSave />
            {saving ? "Enregistrement..." : "Enregistrer l’historique"}
          </button>
        </div>
      </div>
    </form>
  );
}